import { distance, plus, type P3, type PartList } from './vehicle-parts';

/**
 * 乗客の姿勢。
 *
 * - `seated`：座って、手を膝（安全バー）の上に置く
 * - `arms-up`：座って、両手を上げる（コースターの楽しみ方）
 * - `driver`：座って、両手でハンドルを握る
 * - `cyclist`：自転車：前かがみで、手はハンドル、足はペダル
 * - `tuck`：ボブスレー：深く前かがみになり、頭を低くする
 * - `standing`：立つ（機関車の運転台）
 */
export type PersonPose = 'seated' | 'arms-up' | 'driver' | 'cyclist' | 'tuck' | 'standing';

/** 人の見た目（肌・髪・服の色、髪型、体格、帽子・ヘルメット）。番号から決定的に選ぶ。 */
export interface PersonLook {
  readonly skin: number;
  readonly hair: number;
  readonly hairStyle: 'short' | 'long' | 'bun' | 'bald';
  readonly shirt: number;
  readonly pants: number;
  readonly shoes: number;
  /** 体格の倍率（0.9〜1.05） */
  readonly size: number;
  /** 長袖か */
  readonly longSleeves: boolean;
  /** かぶりもの */
  readonly headwear: 'none' | 'helmet' | 'cap';
  /** ヘルメット・帽子の色 */
  readonly headwearColor: number;
}

const SKINS = [0xf6d5c0, 0xeec1a0, 0xd9a07a, 0xb57a52, 0x8d5a3b, 0x5e3a24];
const HAIRS = [0x1b1b1b, 0x2f1d12, 0x5a3820, 0x8a5a2b, 0xc89b5a, 0xa0a0a0, 0x7a2e12];
const SHIRTS = [
  0xd94141, 0x2f6fd0, 0xf2b705, 0x2e9e5b, 0x8e44ad, 0xf2f2f2, 0x1f2937, 0xf08a24, 0x14b8a6,
  0xe75480,
];
const PANTS = [0x2b3a55, 0x1f1f1f, 0x4b5563, 0x7c5c3b, 0x355e8c, 0xbfa880];
const STYLES: PersonLook['hairStyle'][] = [
  'short',
  'long',
  'short',
  'bun',
  'short',
  'long',
  'bald',
];

/** 番号から、人の見た目を決定的に選ぶ（同じ番号なら同じ見た目）。 */
export function personLook(
  index: number,
  headwear: PersonLook['headwear'] = 'none',
  headwearColor = 0xffffff,
): PersonLook {
  const h = hash(index);
  return {
    skin: pick(SKINS, h),
    hair: pick(HAIRS, h >>> 3),
    hairStyle: pick(STYLES, h >>> 6),
    shirt: pick(SHIRTS, h >>> 9),
    pants: pick(PANTS, h >>> 13),
    shoes: (h >>> 16) % 3 === 0 ? 0xf2f2f2 : 0x222222,
    size: 0.9 + (((h >>> 18) % 16) / 15) * 0.15,
    longSleeves: (h >>> 22) % 2 === 0,
    headwear,
    headwearColor,
  };
}

/** 姿勢ごとの手足の位置（腰を原点、前方 −Z）。 */
export interface PoseTargets {
  /** 腰（骨盤）から肩の中心へ */
  readonly shoulders: P3;
  /** 左右の手（右 = +X）。腰からの位置 */
  readonly hands: readonly [P3, P3];
  /** 左右の膝。腰からの位置 */
  readonly knees: readonly [P3, P3];
  /** 左右の足首。腰からの位置 */
  readonly feet: readonly [P3, P3];
}

/** 標準の座った姿勢の、手足の位置（腰からの相対位置）。 */
export function poseTargets(pose: PersonPose, size = 1): PoseTargets {
  const k = size;
  const side = (x: number): [number, number] => [-x * k, x * k];
  const [lx, rx] = side(0.1);
  switch (pose) {
    case 'seated':
      return {
        shoulders: [0, 0.52 * k, 0.04 * k],
        hands: [
          [-0.16 * k, 0.12 * k, -0.34 * k],
          [0.16 * k, 0.12 * k, -0.34 * k],
        ],
        knees: [
          [lx, 0.02 * k, -0.44 * k],
          [rx, 0.02 * k, -0.44 * k],
        ],
        feet: [
          [lx, -0.4 * k, -0.5 * k],
          [rx, -0.4 * k, -0.5 * k],
        ],
      };
    case 'arms-up':
      return {
        ...poseTargets('seated', size),
        hands: [
          [-0.3 * k, 1.12 * k, -0.05 * k],
          [0.3 * k, 1.12 * k, -0.05 * k],
        ],
      };
    case 'driver':
      return {
        ...poseTargets('seated', size),
        shoulders: [0, 0.5 * k, 0.1 * k],
        hands: [
          [-0.17 * k, 0.4 * k, -0.48 * k],
          [0.17 * k, 0.4 * k, -0.48 * k],
        ],
        knees: [
          [lx, 0.12 * k, -0.48 * k],
          [rx, 0.12 * k, -0.48 * k],
        ],
        feet: [
          [lx, -0.18 * k, -0.86 * k],
          [rx, -0.18 * k, -0.86 * k],
        ],
      };
    case 'cyclist':
      // 自転車のサドル・ハンドル・ペダルの位置に合わせる（体格によらない）
      return {
        shoulders: [0, 0.36, -0.4],
        hands: [
          [-0.2, 0, -0.67],
          [0.2, 0, -0.67],
        ],
        // ペダルをこぐ：左足は前・上、右足は後ろ・下
        knees: [
          [-0.1, -0.08, -0.42],
          [0.1, -0.3, -0.3],
        ],
        feet: [
          [-0.1, -0.5, -0.255],
          [0.1, -0.8, -0.085],
        ],
      };
    case 'tuck':
      return {
        shoulders: [0, 0.22 * k, -0.42 * k],
        hands: [
          [-0.16 * k, -0.02 * k, -0.55 * k],
          [0.16 * k, -0.02 * k, -0.55 * k],
        ],
        knees: [
          [lx, 0.28 * k, -0.3 * k],
          [rx, 0.28 * k, -0.3 * k],
        ],
        feet: [
          [lx, -0.05 * k, -0.48 * k],
          [rx, -0.05 * k, -0.48 * k],
        ],
      };
    case 'standing':
      return {
        shoulders: [0, 0.55 * k, 0],
        hands: [
          [-0.2 * k, 0.2 * k, -0.3 * k],
          [0.22 * k, 0.05 * k, -0.05 * k],
        ],
        knees: [
          [lx, -0.45 * k, -0.04 * k],
          [rx, -0.45 * k, 0.02 * k],
        ],
        feet: [
          [lx, -0.88 * k, 0],
          [rx, -0.88 * k, 0.04 * k],
        ],
      };
  }
}

/**
 * 人を部品の集まりに加える。`hip` は腰（骨盤の中心）の位置で、前方（−Z）を向く。
 * 頭（顔・目・鼻・耳・髪）・首・胴（胸・腹）・肩・上腕・前腕・手・腿・すね・靴を、
 * 姿勢 {@link PersonPose} に合わせて置く。
 */
export function addPerson(
  parts: PartList,
  hip: P3,
  pose: PersonPose,
  look: PersonLook,
  options: { readonly legs?: boolean } = {},
): void {
  const k = look.size;
  const t = poseTargets(pose, k);
  const shoulderCenter = plus(hip, t.shoulders);
  const shoulderHalf = 0.19 * k;
  const shoulders: [P3, P3] = [
    plus(shoulderCenter, [-shoulderHalf, -0.02 * k, 0]),
    plus(shoulderCenter, [shoulderHalf, -0.02 * k, 0]),
  ];
  const sleeve = look.shirt;

  // 胴：腹（腰〜胸）と胸（肩のあたり）。腰は服（ズボン）
  parts.capsule(
    plus(hip, [0, 0.06 * k, 0]),
    plus(shoulderCenter, [0, -0.12 * k, 0]),
    0.15 * k,
    sleeve,
    'matte',
  );
  parts.capsule(shoulders[0], shoulders[1], 0.085 * k, sleeve, 'matte');
  parts.capsule(
    plus(hip, [-0.1 * k, 0, 0]),
    plus(hip, [0.1 * k, 0, 0]),
    0.13 * k,
    look.pants,
    'matte',
  );

  // 首と頭（顔は前方 −Z）
  const toward = direction(hip, shoulderCenter);
  const neckBase = plus(shoulderCenter, [0, 0.02 * k, 0]);
  const head = plus(neckBase, scale(toward, 0.19 * k));
  parts.capsule(neckBase, plus(neckBase, scale(toward, 0.08 * k)), 0.05 * k, look.skin, 'matte');
  parts.sphere(
    0.105 * k,
    look.skin,
    'matte',
    { position: head, scale: [0.95, 1.12, 1.02] },
    [10, 7],
  );
  // 目・鼻・耳
  for (const side of [-1, 1]) {
    parts.sphere(
      0.014 * k,
      0x1a1a1a,
      'paint',
      {
        position: plus(head, [side * 0.036 * k, 0.018 * k, -0.098 * k]),
      },
      [5, 3],
    );
    parts.sphere(
      0.022 * k,
      look.skin,
      'matte',
      {
        position: plus(head, [side * 0.1 * k, 0, 0]),
        scale: [0.5, 1, 0.8],
      },
      [5, 3],
    );
  }
  parts.sphere(
    0.018 * k,
    look.skin,
    'matte',
    {
      position: plus(head, [0, -0.01 * k, -0.108 * k]),
      scale: [0.8, 1.1, 1],
    },
    [5, 3],
  );
  parts.box([0.04 * k, 0.006 * k, 0.01 * k], 0x9b4a4a, 'matte', {
    position: plus(head, [0, -0.05 * k, -0.098 * k]),
  });

  // 髪・ヘルメット・帽子
  if (look.headwear === 'helmet') {
    parts.sphere(
      0.13 * k,
      look.headwearColor,
      'paint',
      {
        position: plus(head, [0, 0.04 * k, 0.01 * k]),
        scale: [1, 0.85, 1.12],
      },
      [10, 6],
    );
    parts.box([0.2 * k, 0.04 * k, 0.03 * k], 0x222222, 'glass', {
      position: plus(head, [0, 0.03 * k, -0.115 * k]),
      rotation: [0.3, 0, 0],
    });
  } else if (look.headwear === 'cap') {
    parts.sphere(
      0.115 * k,
      look.headwearColor,
      'matte',
      {
        position: plus(head, [0, 0.055 * k, 0]),
        scale: [1, 0.6, 1.05],
      },
      [10, 6],
    );
    parts.box([0.17 * k, 0.012 * k, 0.09 * k], look.headwearColor, 'matte', {
      position: plus(head, [0, 0.06 * k, -0.12 * k]),
    });
  } else if (look.hairStyle !== 'bald') {
    parts.sphere(
      0.112 * k,
      look.hair,
      'matte',
      {
        position: plus(head, [0, 0.03 * k, 0.012 * k]),
        scale: [1, 0.95, 1.05],
      },
      [10, 6],
    );
    if (look.hairStyle === 'long') {
      parts.box([0.2 * k, 0.24 * k, 0.07 * k], look.hair, 'matte', {
        position: plus(head, [0, -0.08 * k, 0.07 * k]),
      });
    } else if (look.hairStyle === 'bun') {
      parts.sphere(
        0.05 * k,
        look.hair,
        'matte',
        { position: plus(head, [0, 0.07 * k, 0.11 * k]) },
        [6, 4],
      );
    }
  }

  // 腕：上腕と前腕（肘は、肩と手の間で、外側・下へ少し曲げる）
  for (const [i, shoulder] of shoulders.entries()) {
    const hand = plus(hip, t.hands[i] ?? [0, 0, 0]);
    const outward = i === 0 ? -1 : 1;
    const elbow = plus(midpoint(shoulder, hand), [outward * 0.06 * k, -0.06 * k, 0.04 * k]);
    parts.capsule(shoulder, elbow, 0.048 * k, sleeve, 'matte');
    parts.capsule(elbow, hand, 0.04 * k, look.longSleeves ? sleeve : look.skin, 'matte');
    parts.sphere(0.045 * k, look.skin, 'matte', { position: hand, scale: [0.9, 0.8, 1.2] }, [6, 4]);
  }

  // 脚：腿・すね・靴（自転車でこぐ人の脚は、ペダルの位置に合わせて、別に加える）
  if (options.legs ?? true) {
    for (const i of [0, 1] as const) {
      const hipJoint = plus(hip, [(i === 0 ? -0.1 : 0.1) * k, 0, 0]);
      addLeg(parts, hipJoint, plus(hip, t.knees[i]), plus(hip, t.feet[i]), look);
    }
  }
}

/** 脚 1 本：腿（股関節 → 膝）・すね（膝 → 足首）・靴。 */
function addLeg(parts: PartList, hipJoint: P3, knee: P3, foot: P3, look: PersonLook): void {
  const k = look.size;
  parts.capsule(hipJoint, knee, 0.075 * k, look.pants, 'matte');
  parts.capsule(knee, foot, 0.058 * k, look.pants, 'matte');
  parts.box([0.1 * k, 0.07 * k, 0.24 * k], look.shoes, 'matte', {
    position: plus(foot, [0, -0.02 * k, -0.07 * k]),
  });
}

/** 腿・すねの長さ [m]（自転車でこぐ姿勢の、静止の姿勢の骨格から）。 */
const THIGH = 0.43;
const SHIN = 0.45;

/**
 * 自転車をこぐ人の脚を加える。足首を左右のペダル（`pedals[0]` が左）に置き、膝は、腿とすねの長さから
 * 2 関節の逆運動学で求める（膝は前へ、少し外へ曲がる）。ペダルが遠すぎるときは、足首をペダルの方へ
 * 届く範囲に引き戻す。
 *
 * @param hip 腰（骨盤の中心）の位置（{@link addPerson} と同じ）
 */
export function addCyclistLegs(
  parts: PartList,
  hip: P3,
  look: PersonLook,
  pedals: readonly [P3, P3],
): void {
  for (const i of [0, 1] as const) {
    const side = i === 0 ? -1 : 1;
    const hipJoint = plus(hip, [side * 0.1 * look.size, 0, 0]);
    const pedal = pedals[i];
    const reach = distance(hipJoint, pedal);
    const d = Math.min(THIGH + SHIN - 0.005, Math.max(Math.abs(THIGH - SHIN) + 0.005, reach));
    const u = scale(minus(pedal, hipJoint), 1 / Math.max(1e-6, reach));
    const foot = plus(hipJoint, scale(u, d));
    // 膝の向き：前（−Z）を、脚の軸に垂直な成分にして、少し外へ
    const forward: P3 = [0, 0, -1];
    const along = dot(forward, u);
    const bend = normalize(plus(minus(forward, scale(u, along)), [side * 0.18, 0, 0]));
    const a = (THIGH * THIGH - SHIN * SHIN + d * d) / (2 * d);
    const h = Math.sqrt(Math.max(0, THIGH * THIGH - a * a));
    const knee = plus(plus(hipJoint, scale(u, a)), scale(bend, h));
    addLeg(parts, hipJoint, knee, foot, look);
  }
}

function minus(a: P3, b: P3): P3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function dot(a: P3, b: P3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function normalize(a: P3): P3 {
  const length = Math.hypot(a[0], a[1], a[2]);
  return length < 1e-9 ? [0, 0, -1] : scale(a, 1 / length);
}

function hash(index: number): number {
  let x = (index + 1) * 0x9e3779b1;
  x ^= x >>> 15;
  x = Math.imul(x, 0x85ebca6b);
  x ^= x >>> 13;
  return x >>> 0;
}

function pick<T>(list: readonly T[], value: number): T {
  return list[value % list.length] as T;
}

function midpoint(a: P3, b: P3): P3 {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];
}

function scale(v: P3, s: number): P3 {
  return [v[0] * s, v[1] * s, v[2] * s];
}

function direction(a: P3, b: P3): P3 {
  const d: P3 = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
  const length = Math.hypot(...d) || 1;
  return [d[0] / length, d[1] / length, d[2] / length];
}
